import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { Answers } from "./screenerSteps";

interface ChoiceButtonsProps {
  step: import("./screenerSteps").Step;
  answers: Answers;
  onAnswer: (id: string, value: string | string[]) => void;
  onNext: () => void;
}

export function ChoiceButtons({
  step,
  answers,
  onAnswer,
  onNext,
}: ChoiceButtonsProps) {
  const isMulti = step.type === "multi";
  const currentVal = answers[step.id];
  const selectedVals: string[] = isMulti
    ? Array.isArray(currentVal)
      ? currentVal
      : currentVal
        ? (currentVal as string).split(",")
        : []
    : [];
  const isAnswered = isMulti ? selectedVals.length > 0 : !!currentVal;

  return (
    <div className="space-y-5">
      <h2 className="font-headings text-lg font-bold text-primary sm:text-xl">
        {step.question}
      </h2>
      {step.helpText && (
        <p className="text-sm text-muted-foreground">{step.helpText}</p>
      )}
      <div className="space-y-3">
        {step.options?.map((opt) => {
          const selected = isMulti
            ? selectedVals.includes(opt.value)
            : currentVal === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                if (isMulti) {
                  const next = selected
                    ? selectedVals.filter((v) => v !== opt.value)
                    : [...selectedVals, opt.value];
                  onAnswer(step.id, next.join(","));
                } else {
                  onAnswer(step.id, opt.value);
                  // Auto-advance on single choice
                  setTimeout(() => onNext(), 200);
                }
              }}
              className={`flex w-full items-center justify-between rounded-xl border-2 px-5 py-4 text-left text-base font-semibold transition-all ${
                selected
                  ? "border-accent bg-accent/10 text-primary"
                  : "border-border bg-background text-primary hover:border-accent/40"
              }`}
            >
              {opt.label}
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                  selected ? "border-accent bg-accent" : "border-border"
                }`}
              >
                {selected && (
                  <span className="h-2 w-2 rounded-full bg-accent-foreground" />
                )}
              </span>
            </button>
          );
        })}
      </div>
      {isMulti && (
        <Button
          type="button"
          onClick={onNext}
          disabled={!isAnswered}
          size="lg"
          className="w-full bg-gradient-accent font-bold text-accent-foreground shadow-glow hover:opacity-90 disabled:opacity-50"
        >
          Continue
          <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
